// Pure decision logic shared by server and browser: scoring places, grouping them into areas and
// picking the focus. No I/O.
import type { Policy } from './feynman.ts';
import { geometricMedian, haversine, type LatLon } from './geo.ts';

export type TravelMode = 'foot' | 'bike' | 'car' | 'transit';

/** Someone taking part; without a location they don't count this round. */
export interface Person {
  id: string;
  lat: number | null;
  lon: number | null;
  mode: TravelMode;
}

export interface Place extends LatLon {
  id: string;
  name: string;
  extId?: string | null;
}

export interface VoteRow {
  memberId: string;
  candidateId: string;
  value: number;
}

export interface Visited extends LatLon {
  extId?: string | null;
}

/** How the group rated a place after going there ("would you go again?"). */
export interface Remembered extends LatLon {
  extId?: string | null;
  yes: number;
  maybe: number;
  no: number;
}

/** `${memberId}|${candidateId}` -> seconds (null = unroutable/unknown). */
export type TimeTable = Map<string, number | null>;

export interface ScoreOptions {
  /** Ratings of places the group has been to, shown with each place. */
  memory?: Remembered[];
  /** Feynman: try new places, or go back to the best one once it is good enough. */
  policy?: Policy;
}

export type Scored<P extends Place> = P & {
  times: Record<string, number>;
  maxTime: number;
  avgTime: number;
  estimated: boolean;
  up: string[];
  down: string[];
  support: number;
  /** The group knows this place already. */
  visited: boolean;
  /** Feynman says: go back here. */
  pick: boolean;
  memory: Remembered | null;
  score: number;
  rank: number;
};

export interface Area extends LatLon {
  radius: number;
  weight: number;
  votes: number;
  bestScore: number;
  candidateIds: string[];
}

export interface Focus extends LatLon {
  radius: number;
  from: 'votes+times' | 'fair-middle';
}

// Door-to-door straight-line speeds (m/s) used until real routing answers.
const SPEED: Record<TravelMode, number> = { foot: 1.1, bike: 3.3, car: 7, transit: 4.5 };
export const estimate = (from: LatLon, to: LatLon, mode: TravelMode) =>
  Math.round((haversine(from, to) * 1.3) / SPEED[mode] + 90);

export const timeKey = (memberId: string, candidateId: string) => `${memberId}|${candidateId}`;

export function located(people: Person[]): (Person & LatLon)[] {
  return people.filter((p): p is Person & LatLon => p.lat != null && p.lon != null);
}

export function fairCenter(people: Person[]): LatLon | null {
  return geometricMedian(located(people));
}

export const samePlace = (a: LatLon & { extId?: string | null }, b: LatLon & { extId?: string | null }) =>
  (!!a.extId && !!b.extId && a.extId === b.extId) || haversine(a, b) < 100;

export function isVisited(place: LatLon & { extId?: string | null }, visited: Visited[] = []) {
  return visited.some((v) => samePlace(v, place));
}

export function memoryOf(place: LatLon & { extId?: string | null }, memory: Remembered[] = []) {
  return memory.find((m) => samePlace(m, place)) ?? null;
}

export function scoreCandidates<P extends Place>(
  people: Person[],
  candidates: P[],
  times: TimeTable,
  votes: VoteRow[],
  opts: ScoreOptions = {},
): Scored<P>[] {
  const here = located(people);
  const voters = new Set(here.map((p) => p.id));
  const n = Math.max(1, voters.size);
  const scored = candidates.map((c) => {
    const t: Record<string, number> = {};
    let max = 0;
    let sum = 0;
    let estimated = false;
    for (const p of here) {
      let s = times.get(timeKey(p.id, c.id));
      if (s == null) {
        s = estimate(p, c, p.mode);
        estimated = true;
      }
      t[p.id] = s;
      max = Math.max(max, s);
      sum += s;
    }
    const avg = here.length ? sum / here.length : 0;
    const up = votes
      .filter((v) => v.candidateId === c.id && v.value > 0 && voters.has(v.memberId))
      .map((v) => v.memberId);
    const down = votes
      .filter((v) => v.candidateId === c.id && v.value < 0 && voters.has(v.memberId))
      .map((v) => v.memberId);
    const support = Math.max(-1, Math.min(1, (up.length - down.length) / n));
    const visited = isVisited(c, opts.policy?.known);
    const pick = opts.policy?.stance === 'exploit' && !!opts.policy.best && samePlace(c, opts.policy.best);
    const cost = (0.65 * max + 0.35 * avg) / 60; // minutes, the longest trip first
    // Votes scale the cost superlinearly: a lone vote barely matters (1/3 support = x0.87),
    // consensus dominates (2/3 = x0.57, unanimous = x0.29, i.e. the group accepts ~3.5x the travel).
    const voteFactor = Math.exp(-1.25 * support * Math.abs(support));
    // Feynman: the pick is worth ~40% more travel; while exploring, known places step back.
    const policyFactor = pick ? 0.7 : visited ? 1.25 : 1;
    return {
      ...c,
      times: t,
      maxTime: max,
      avgTime: avg,
      estimated,
      up,
      down,
      support,
      visited,
      pick,
      memory: memoryOf(c, opts.memory),
      score: here.length ? cost * voteFactor * policyFactor : -support,
      rank: 0,
    };
  });
  scored.sort((a, b) => a.score - b.score || b.up.length - a.up.length || a.id.localeCompare(b.id));
  scored.forEach((c, i) => {
    c.rank = i + 1;
  });
  return scored;
}

// Single-link clustering: candidates within `link` metres of each other form one area.
export function areasOf<P extends Place>(scored: Scored<P>[], link = 650): Area[] {
  const clusters: Scored<P>[][] = [];
  for (const c of scored) {
    const near = clusters.filter((cl) => cl.some((x) => haversine(x, c) <= link));
    if (!near.length) {
      clusters.push([c]);
      continue;
    }
    const [first, ...rest] = near;
    first.push(c);
    for (const r of rest) {
      first.push(...r);
      clusters.splice(clusters.indexOf(r), 1);
    }
  }
  if (!clusters.length) return [];
  const best = scored[0].score;
  return clusters
    .map((items) => {
      // Good (low) scores dominate the weight; several decent options together also count.
      const w = items.map((c) => Math.exp(-(c.score - best) / 5));
      const W = w.reduce((a, b) => a + b, 0);
      const lat = items.reduce((s, c, i) => s + c.lat * w[i], 0) / W;
      const lon = items.reduce((s, c, i) => s + c.lon * w[i], 0) / W;
      const center = { lat, lon };
      return {
        lat,
        lon,
        radius: Math.round(Math.max(250, ...items.map((c) => haversine(center, c) + 120))),
        weight: W,
        votes: items.reduce((s, c) => s + c.up.length - c.down.length, 0),
        bestScore: Math.min(...items.map((c) => c.score)),
        candidateIds: items.map((c) => c.id),
      };
    })
    .sort((a, b) => b.weight - a.weight);
}

export function focusOf(people: Person[], areas: Area[]): Focus | null {
  if (areas.length) return { lat: areas[0].lat, lon: areas[0].lon, radius: areas[0].radius, from: 'votes+times' };
  const c = fairCenter(people);
  return c ? { ...c, radius: 800, from: 'fair-middle' } : null;
}
